import React, { useEffect, useState } from 'react';
import { useAuth } from 'services/auth-service';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import BusinessIcon from '@mui/icons-material/Business';
import { useQuery } from 'react-query';
import { BuildingProps } from 'services/hooks/building-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveBuilding from './crud/useSave';
import AccountService from 'services/hooks/account-service';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import InputTimezonePicker from 'components/elements/Form/Input/inputTimezonePicker';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateUpdateErrorMessage
} from 'utils/helpers';
import variables from 'variables';
import { GetNonServerErrorMessage } from 'utils/constants';
import CustomPhoneInput from 'components/elements/Form/Input/PhoneInput';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import ImageUploadInput from 'components/elements/Form/Input/ImageUploadInput';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: (newBuilding: BuildingProps) => Promise<BuildingProps>;
  updateQuery: (uuid: string | undefined, updatedBuilding: BuildingProps) => Promise<BuildingProps>;
  editQuery?: (uuid: string | undefined) => Promise<BuildingProps>;
  editUUID?: string;
  queryName: string;
};

type TimezoneObjectProps = {
  abbrev?: string;
  altName?: string;
  label?: string;
  offset?: number;
  value?: string;
};

/* The `CreateBuildingSchema` constant is defining a validation schema using the `yup` library. It
specifies the validation rules for the fields `accountUuid`, `name`, and `address`. */
const CreateBuildingSchema = (user: string | undefined) => {
  const baseSchema = yup.object().shape({
    name: yup.string().required('Name is required'),
    address: yup.string().required('Address is required'),
    phoneNumber: yup.string().test('phone-required', 'Phone number is not valid', function (value) {
      if (value && value.length > 0) {
        return isPossiblePhoneNumber(value);
      }
      return true;
    }),
    email: yup.string().test('email-required', 'Invalid email address', function (value) {
      if (value && value.length > 0) {
        return yup.string().email().isValidSync(value);
      }
      return true; // If not entered, it's valid
    })
  });

  if (user === 'SUPER') {
    return baseSchema.shape({
      accountUuid: yup.string().required('Account is required')
    });
  }

  return baseSchema;
};

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  /* The code is using the `useAuth` hook to access the user's authentication state. It destructures the
`user` property from the state object returned by the `useAuth` hook. */
  const {
    state: { user }
  } = useAuth();
  const [disabled, setDisabled] = useState(false);
  const [getBuildingResult, setGetBuildingResult] = useState<BuildingProps | null>(null);
  const [getTimezoneResult, setGetTimezoneResult] = useState<
    TimezoneObjectProps | string | null | undefined
  >();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  /* The code snippet is using the `useQuery` hook from the `react-query` library to fetch data for a
 building. */
  const { isSuccess, data } = useQuery<BuildingProps | undefined, Error>(
    'building',
    () => (editUUID ? editQuery?.(editUUID) : undefined),
    {
      enabled: editUUID ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  /* The `useForm` hook from the `react-hook-form` library is being used to create a form for Building users with form
  validation. */
  const methods = useForm({
    defaultValues: getBuildingResult
      ? {
          uuid: getBuildingResult?.uuid,
          name: getBuildingResult?.name,
          address: getBuildingResult?.address,
          minFac: getBuildingResult?.minFac,
          maxFac: getBuildingResult?.maxFac,
          minBadge: getBuildingResult?.minBadge,
          maxBadge: getBuildingResult?.maxBadge,
          timezone: getBuildingResult?.timezone,
          accountUuid: getBuildingResult?.accountUuid,
          image: getBuildingResult?.image,
          phoneNumber: getBuildingResult?.phoneNumber,
          email: getBuildingResult?.email
        }
      : {
          uuid: '',
          name: '',
          address: '',
          minFac: '',
          maxFac: '',
          minBadge: '',
          maxBadge: '',
          timezone: '',
          accountUuid: '',
          image: '',
          phoneNumber: '',
          email: ''
        },
    resolver: yupResolver(CreateBuildingSchema(user.role))
  });

  useEffect(() => {
    if (data && editUUID) {
      setGetBuildingResult(data);
      // Set image on Edit mode for image preview
      setSelectedImage(data.image ? variables.RESOURCE_IMAGE_URL + data.image : null);
      /* `methods.reset(data)` is being used to set the initial values of the form fields to the values retrieved from the `data` object. The
      `data` object contains the Building details fetched from the API when editing an existing Building. By calling `methods.reset()` with the
      desired initial values, the form fields will be populated with the fetched data when the form is rendered. */
      methods.reset({
        ...data,
        phoneNumber: data.phoneNumber ? '+' + data.phoneNumber : ''
      });
    }
  }, [data, editUUID, methods]);

  /* The `saveBuildingMutation` constant is using the `useSaveBuilding` hook to create a mutation 
  function for saving a building. It takes in several parameters: */
  const saveBuildingMutation = useSaveBuilding({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setDisabled(false);
      if (setEditUUID) setEditUUID('');
    },
    onError: (error: any) => {
      setDisabled(false);
      if (error.response) {
        if (!editUUID) {
          toast.error(generateSaveErrorMessage('Building'));
        } else {
          toast.error(generateUpdateErrorMessage('Building'));
        }
      } else {
        // Handle cases where the error is not from the server response
        toast.error(GetNonServerErrorMessage);
      }
    }
  });

  /* The line `const timezoneWatch = methods.watch('timezone');` is using the `watch` function from the `react-hook-form` library to track
 changes in the `timezone` field of the form. It returns the current value of the `timezone` field, which is then stored in the `timezoneWatch`
 variable. This allows you to access the current value of the `timezone` field and perform any necessary actions or updates based on its value. */
  const timezoneWatch: TimezoneObjectProps | string | null | undefined = methods.watch('timezone');

  useEffect(() => {
    if (timezoneWatch) {
      setGetTimezoneResult(timezoneWatch);
    }
  }, [timezoneWatch]);

  /* The function `getTimezoneValue` returns the timezone identifier if it is a string, otherwise it returns the `value` property of the timezone 
  object. */
  const getTimezoneValue = (
    timezone: TimezoneObjectProps | string | null | undefined
  ): string | null | undefined => {
    if (typeof timezone === 'string') {
      // When in Edit mode, directly return the timezone identifier
      return timezone;
    }

    // When creating a new Building, use the 'value' property
    return timezone?.value;
  };

  /**
   * The `onSubmit` function is used to handle form submission and save building data, including the account ID, timezone, and other properties.
   * BuildingProps - an object containing the properties of a building, such as uuid, name, address, bitformat, minFac, maxFac, minBadge,
   * maxBadge, and timezone. */
  const onSubmit = async (e: BuildingProps) => {
    setDisabled(true);

    let accountId = '';
    if (user.role === 'SUPER' || user.role === 'BUILDING') {
      accountId = e?.accountUuid || '';
    } else if (user.role === 'ACCOUNT') {
      accountId = user?.uuid ? user?.uuid : '';
    }

    const tempTimezone: string | null | undefined = getTimezoneValue(getTimezoneResult);

    saveBuildingMutation.mutate({
      uuid: e?.uuid,
      name: e?.name,
      address: e?.address,
      accountUuid: accountId,
      bitformat: e?.bitformat ? e.bitformat : 'W26-Pier',
      minFac: !isNaN(Number(e?.minFac)) ? Number(e?.minFac) : undefined,
      maxFac: !isNaN(Number(e?.maxFac)) ? Number(e?.maxFac) : undefined,
      minBadge: !isNaN(Number(e?.minBadge)) ? Number(e?.minBadge) : undefined,
      maxBadge: !isNaN(Number(e?.maxBadge)) ? Number(e?.maxBadge) : undefined,
      timezone: tempTimezone
        ? tempTimezone
        : Intl.DateTimeFormat().resolvedOptions().timeZone
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null,
      image: e?.image ? e?.image : undefined,
      phoneNumber: e?.phoneNumber?.replace(/^\+/, ''),
      email: e?.email
    });
  };

  /* The above code is a TypeScript React component that renders a form inside a modal. */
  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            {user.role === 'SUPER' && (
              <SelectEntity
                Controller={Controller}
                control={methods.control}
                label="Choose an Account*"
                placeholder="Select Account"
                queryname="accounts"
                name="accountUuid"
                defaultValue=""
                errors={methods.formState.errors}
                disabled={editUUID && true}
                query={AccountService.findAllAccounts}
              />
            )}
            <Input
              label="Building Name*"
              name="name"
              type="text"
              placeholder="Bline"
              autoFocus
              methods={methods}
            />
            <Input
              label="Address*"
              name="address"
              type="text"
              placeholder="1209 Marginal Road"
              methods={methods}
            />
            <CustomPhoneInput
              name="phoneNumber"
              label="Company Phone Number"
              defaultCountry="CA"
              control={methods.control}
              errors={methods.formState.errors}
              placeholder="Enter Phone Number"
            />
            <Input
              label="Company Email"
              name="email"
              type="email"
              placeholder="sam.smith@b-line.io"
              autoFocus
              methods={methods}
            />
            <div className="flex space-x-4">
              <Input
                label="MinFac"
                name="minFac"
                type="number"
                defaultValue={1}
                placeholder="1"
                methods={methods}
              />
              <Input
                label="MaxFac"
                name="maxFac"
                type="number"
                defaultValue={255}
                placeholder="255"
                methods={methods}
              />
            </div>
            <div className="flex space-x-4">
              <Input
                label="MinBadge"
                name="minBadge"
                type="number"
                defaultValue={1}
                placeholder="1"
                methods={methods}
              />
              <Input
                label="MaxBadge"
                name="maxBadge"
                type="number"
                defaultValue={65535}
                placeholder="65535"
                methods={methods}
              />
            </div>
            <InputTimezonePicker
              Controller={Controller}
              control={methods.control}
              label="Select Timezone*"
              placeholder="Select Timezone"
              name="timezone"
            />
            <ImageUploadInput
              methods={methods}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
              name="image"
              label="Image"
              ratioInfo="For best display, use images with a ~12:5 ratio (e.g., 2400px x 1000px)."
              error={methods.formState.errors.image}
            />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            iconLeft={<BusinessIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
